import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

// axios
import { api } from "@/api/axios";

// Context
import { useAuth } from '@/auth/AuthContext';

// token
import { getAccessToken } from "../auth/token";

export default function useSSE ({ url, sid }) {
  const { pathname } = useLocation();
  const {setMessageNotify, setTotalQuantity} = useAuth(); // Галочки для обратной связи и кол-во услуг в корзине
  const [notifications, setNotifications] = useState([]); // Уведомления

  const eventSourseRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const currentReconnectIntervalRef = useRef(1000);
  const lastEventId = useRef(null);
  const openedNotifiedRef = useRef(false);
  const accessToken = useRef(getAccessToken()) || null;

  // Переподключение
  const baseReconnectInterval = 1000; // ms
  const maxReconnectInterval = 30000; // ms

  const notifyClosed = useCallback(async (useBeacon = false) => {
    const payload = {
      SESSION_ID: sid,
      STATUS: "0"
    }

    if (navigator.sendBeacon && useBeacon) {
      fetch('/gates/data/set_active_sse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer_${accessToken}`,
        },
        body: JSON.stringify(payload),
        keepalive: true, // позволит запросу завершиться после закрытия страницы
      })
        .catch(err => console.error('offline fetch error', err));

      sseSourse.current.close();
    } else {
      try {
        const res = await api.post('/gates/data/set_active_sse', payload);
      } catch (error) {
        console.error('Error: ', error);
      }
    }
  }, [sid])

  const notifyOpened = async () => {
    if (openedNotifiedRef.current) return; // Защита от повторных вызовов метода set_active_sse
    openedNotifiedRef.current = true;

    const payload = {
      SESSION_ID: sid,
      STATUS: "1"
    }
    
    try {
      const res = await api.post('/gates/data/set_active_sse', payload);
    } catch (error) {
      console.error('Error: ', error);
    }
  }

  const closeConnection = useCallback(async () => {
    if (eventSourseRef.current) {
      eventSourseRef.current.close();
      eventSourseRef.current = null;
      await notifyClosed(false);
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // если соединение уже открыто - закрываем
    closeConnection();

    if (!sid) {
      return;
    }

    const sseURL = new URL(url, window.location.origin);
    sseURL.searchParams.set('sid', sid);
    sseURL.searchParams.set('last-event-id', lastEventId.current);

    try {
      const sseSourse = new EventSource(sseURL);
      eventSourseRef.current = sseSourse;
      openedNotifiedRef.current = false;

      // Обработчик открытия соединения
      sseSourse.onopen = () => {
        notifyOpened();
        currentReconnectIntervalRef.current = baseReconnectInterval;
      };

      // Обработчик всех сообщений(пока что получаем все, затем надо будет переписать на получение определенного события условно lk_notify)
      sseSourse.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (!(Object.keys(data).length === 0)) {
            lastEventId.current = data.id;
            if (data.data.TYPE === "feedback" && pathname === '/chatFeedBack') {
              setMessageNotify((prev) => [...prev, data]);
            } 
            else if (data.data.TYPE === "basket") {
              setTotalQuantity(data.data.TOTAL);
            } else {
              setNotifications((prev) => [...prev, data]);
            }           

          }
        } catch (error) {
          console.error('Ошибка при обработке сообщения: ', error);
        }
      };

      sseSourse.onerror = (error) => {
        console.error('SSE ошибка: ', error);

        closeConnection();

        if (!reconnectTimeoutRef.current) {
          const nextInterval = Math.min(currentReconnectIntervalRef.current, maxReconnectInterval);

          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null;

            currentReconnectIntervalRef.current = Math.min(currentReconnectIntervalRef.current * 2, maxReconnectInterval);
            connect();
          }, nextInterval);
        }
      };

    } catch (error) {
      console.error('Ошибка создания EventSourse: ', error);
    }
  }, [url, closeConnection, sid]);

  useEffect(() => {
    const handleBeforeUnload = async () => {
      alert('Отработала функция handleBeforeUnload');
      await notifyClosed(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [notifyClosed]);

  useEffect(() => {
    connect();
    return () => closeConnection();
  }, [connect, closeConnection]);
  
  // Функция для ручного переподключения
  const reconnect = useCallback(() => {
    closeConnection();
    currentReconnectIntervalRef.current = baseReconnectInterval;
    connect();
  });

  return {
    notifications, 
    reconnect,
    closeConnection,
  }
}